import {
  Locale,
  shopConfigByLocale,
  useConfig,
  useHostName,
  usePublicProtocol,
} from '@aboutyou/config';
import {useLocale, useTranslation} from '@aboutyou/i18n';
import {useCheckoutRoute} from '@aboutyou/router';
import {useTrackExpressCheckoutEvents} from '@aboutyou/tracking';
import {Text as MiniTermsText, useShowToast} from '@aboutyou/ui-components';
import React from 'react';
import {ExpressCheckout} from './ExpressCheckout/ExpressCheckout';
import {Payment, SuccessHandlerEvent} from './ExpressCheckout/types';
import {useExpressCheckout} from './ExpressCheckout/useExpressCheckout';

import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Log} from '@aboutyou/mlm';
import {useCampaignKey} from '../../backend-services/Campaigns/campaign-state/campaign.hooks';
import {PayPalCheckoutButton} from './PayPalCheckoutButton';

interface PayPalCheckoutButtonWrapperProps {
  source: string;
  basketId: string;
  totalPrice: number;
  customerId: number | null;
  onExpressPaymentStart?: VoidFunction;
  onExpressPaymentEnd?: VoidFunction;
  className?: string;
}

export const PayPalCheckoutButtonWrapper: React.FC<
  PayPalCheckoutButtonWrapperProps
> = props => {
  const showToast = useShowToast();
  const trackExpressCheckoutEvents = useTrackExpressCheckoutEvents();
  const {t} = useTranslation();
  const locale = useLocale();
  const {shopId: platformAppId} = shopConfigByLocale[locale];
  const config = useConfig();
  const baseUrl = config.checkoutAssetsUrl;
  const expressCheckoutSdk = useExpressCheckout();
  const hasWithdrawal = ![Locale.de_CH, Locale.fr_CH].includes(locale);
  const checkoutRoute = useCheckoutRoute();
  const hostname = useHostName();
  const protocol = usePublicProtocol();
  const {activateAbTest} = useIsFeatureActive('basket_v2_express_checkout');
  const campaignKey = useCampaignKey();

  const miniTermsContent = hasWithdrawal
    ? t(
        'The general terms and conditions of ABOUT YOU also apply with regard to the right of withdrawal',
      )
    : t('The terms and conditions of ABOUT YOU apply');

  if (expressCheckoutSdk.isLoading) {
    return null;
  }

  const sendErrorToast = (errorCode: number) => {
    if ([403, 410, 424].includes(errorCode)) {
      showToast({
        type: 'FAILURE',
        content: t(
          'Sorry, PayPal is unavailable at the moment. Please proceed by clicking the Checkout button.',
        ),
        'data-testid': 'paypalErrorToast',
      });
    }
  };

  return (
    <>
      <ExpressCheckout
        {...props}
        sdk={expressCheckoutSdk.data!}
        platformAppId={platformAppId}
        baseUrl={baseUrl}
        session={decodeURIComponent('')}
        checkoutRoute={`${protocol}://${hostname}${checkoutRoute}`}
        device={config.device}
        campaignKey={campaignKey}
        onPaymentStart={() => {
          if (props.onExpressPaymentStart) {
            props.onExpressPaymentStart();
          }
          Log.info(
            '@aboutyou/logic-components',
            `<express-checkout>: payment start`,
          );

          trackExpressCheckoutEvents('submit', 'paypal_instant');
        }}
        onPaymentSuccess={(event: SuccessHandlerEvent) => {
          Log.info(
            '@aboutyou/logic-components',
            `<express-checkout>: payment success`,
          );

          Log.compare({
            actionName: 'PaypalExpress',
            success: true,
            attributes: {},
          });

          // redirect to the OSP page
          window.location.href = event.data.redirectUrl;
        }}
        onPaymentError={event => {
          if (props.onExpressPaymentEnd) {
            props.onExpressPaymentEnd();
          }
          Log.compare({
            actionName: 'PaypalExpress',
            success: false,
            attributes: {
              statusCode: event.data.statusCode,
              message: event.data.message,
            },
          });

          trackExpressCheckoutEvents('close', 'paypal_instant');
          sendErrorToast(event.data.statusCode);
        }}
        onPaymentCanceled={() => {
          if (props.onExpressPaymentEnd) {
            props.onExpressPaymentEnd();
          }
          Log.info(
            '@aboutyou/logic-components',
            `<express-checkout>: payment cancelled`,
          );
          trackExpressCheckoutEvents('close', 'paypal_instant');
        }}
      >
        {({data}) =>
          data?.payment.key === Payment.PAYPAL && (
            <div className={props.className}>
              <PayPalCheckoutButton
                data={data}
                sdk={expressCheckoutSdk.data!}
                source={props.source}
                onButtonMount={() => {
                  Log.info(
                    '@aboutyou/logic-components',
                    `<express-checkout>: button mounted`,
                    {isPaypalButtonMounted: true},
                  );
                  activateAbTest();
                  // TODO: should use useTrackFeatureInViewImpression instead -> https://aboutyou.atlassian.net/jira/software/c/projects/AYFEP/issues/AYFEP-3460
                  trackExpressCheckoutEvents('impression', 'paypal_instant');
                }}
              />
              <MiniTermsText size="px10" color="Grey2">
                {miniTermsContent}
              </MiniTermsText>
            </div>
          )
        }
      </ExpressCheckout>
    </>
  );
};
